import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * ChunkBuilder — "The Bridge" builder for the want/like/can lesson.
 *
 * Power-verb tabs on top (أُرِيدُ، أُحِبُّ، أَسْتَطِيعُ), ready-made أَنْ
 * chunks below. Tapping a chunk snaps it onto the active power verb — the
 * power verb in gold, the أَنْ bridge in violet — and plays the sentence.
 * Completes when every chunk has been tried on every power verb.
 *
 * Lesson 23 rebuilds it as a polite-request builder: its combos carry no
 * أَنْ bridge, so the opener is drawn in gold and the shared `data.tail`
 * (مِنْ فَضْلِكَ) in violet instead.
 */
const AN = "أَنْ";

function AssembledSentence({ combo, power, tail }) {
  const afterPower =
    combo.ar.startsWith(power.ar) ? combo.ar.slice(power.ar.length + 1) : null;
  if (!afterPower) {
    return <>{combo.ar}</>;
  }
  if (afterPower.startsWith(`${AN} `)) {
    const rest = afterPower.slice(AN.length + 1);
    return (
      <>
        <span style={{ color: "var(--gold-bright)" }}>{power.ar}</span>{" "}
        <span style={{ color: "var(--tag-violet)" }}>{AN}</span> {rest}
      </>
    );
  }
  const tailAt = tail ? afterPower.lastIndexOf(tail) : -1;
  if (tailAt === -1) {
    return (
      <>
        <span style={{ color: "var(--gold-bright)" }}>{power.ar}</span> {afterPower}
      </>
    );
  }
  return (
    <>
      <span style={{ color: "var(--gold-bright)" }}>{power.ar}</span>{" "}
      {afterPower.slice(0, tailAt)}
      <span style={{ color: "var(--tag-violet)" }}>{tail}</span>
      {afterPower.slice(tailAt + tail.length)}
    </>
  );
}

export default function ChunkBuilder({ data, onComplete, audio }) {
  const [powerIdx, setPowerIdx] = useState(0);
  const [chunkId, setChunkId] = useState(null);
  const [visited, setVisited] = useState(() => new Set());

  const power = data.powers[powerIdx];
  const total = data.powers.length * data.chunks.length;
  const done = visited.size >= total;
  const combo = useMemo(
    () =>
      chunkId
        ? data.combos.find((c) => c.power === power.id && c.chunk === chunkId)
        : null,
    [data.combos, power, chunkId]
  );

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleChunk = (c) => {
    setChunkId(c.id);
    const picked = data.combos.find((x) => x.power === power.id && x.chunk === c.id);
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(`${power.id}:${c.id}`);
      return next;
    });
    if (audio && picked) audio.play(picked.ar);
  };

  const handlePower = (idx) => {
    setPowerIdx(idx);
    setChunkId(null);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex gap-2">
          {data.powers.map((p, idx) => {
            const powerDone = data.chunks.every((c) => visited.has(`${p.id}:${c.id}`));
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePower(idx)}
                className="rounded-lg px-3 py-1.5 transition-all hover:brightness-110"
                style={{
                  background: idx === powerIdx ? "var(--gold-bg)" : "var(--ink)",
                  border: idx === powerIdx ? "1px solid var(--gold-dim)" : "1px solid var(--panel-line)",
                  color: powerDone ? "var(--gold)" : "var(--parchment)",
                }}
              >
                <span lang="ar" dir="rtl" className="font-arabic text-xl leading-[1.6]">{p.ar}</span>
                <span className="text-[11px] block" style={{ color: "var(--parchment-dim)" }}>
                  {p.en}
                  {powerDone && <span aria-hidden="true"> ✓</span>}
                </span>
              </button>
            );
          })}
        </div>
        <ProgressPill>{visited.size}/{total} sentences built</ProgressPill>
      </div>

      <div className="text-center min-h-[9rem]">
        {combo ? (
          <>
            <p dir="rtl" lang="ar" className="font-arabic text-4xl sm:text-5xl leading-[1.8] m-0" style={{ color: "var(--parchment)" }}>
              <AssembledSentence combo={combo} power={power} tail={data.tail} />
            </p>
            <p className="text-base mt-2 mb-0 inline-flex items-center gap-3" style={{ color: "var(--parchment)" }}>
              {combo.en}
              {audio && <PlayButton text={combo.ar} playingText={audio.playingText} onPlay={audio.play} />}
            </p>
            {combo.note && (
              <p className="text-sm mt-2 mb-0 px-4 py-2 rounded-lg inline-block" style={{ background: "var(--gold-bg)", color: "var(--parchment)" }}>
                <MixedText text={combo.note} />
              </p>
            )}
          </>
        ) : (
          <p className="text-base mt-8 mb-0 animate-pulse" style={{ color: "var(--parchment-dim)" }}>
            Pick a chunk below to build the sentence.
          </p>
        )}
      </div>

      <div dir="rtl" className="flex flex-wrap justify-center gap-2 mt-5">
        {data.chunks.map((c) => {
          const seen = visited.has(`${power.id}:${c.id}`);
          const active = chunkId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => handleChunk(c)}
              className="rounded-xl px-3.5 py-2 text-center transition-all hover:brightness-110"
              style={{
                background: active ? "var(--gold-bg)" : "var(--ink)",
                border: active
                  ? "1px solid var(--gold-dim)"
                  : seen
                  ? "1px solid var(--tag-violet)"
                  : "1px solid var(--panel-line)",
              }}
            >
              <span lang="ar" className="font-arabic text-2xl leading-[1.6] block" style={{ color: "var(--parchment)" }}>
                {c.ar}
              </span>
              <span className="text-[11px] block" style={{ color: "var(--parchment-dim)" }}>{c.en}</span>
            </button>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🌉</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
